import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, XCircle, Clock, Search, Filter, RefreshCw, 
  DollarSign, Calendar, User, Phone, Mail, FileText, 
  Download, Eye, AlertTriangle, Loader2, MoreHorizontal 
} from 'lucide-react';

const AdminPaymentApproval = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(null);

  // Fetch bookings from backend
  useEffect(() => {
    fetchBookings();
  }, []);

  // Filter bookings based on search and status
  useEffect(() => {
    let filtered = bookings;
    
    if (searchTerm) {
      filtered = filtered.filter(booking => 
        booking.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.bookingId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.service.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.payment_status === statusFilter);
    }
    
    setFilteredBookings(filtered);
  }, [bookings, searchTerm, statusFilter]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await fetch('http://localhost:5000/api/admin/bookings');
      const result = await response.json();
      
      if (result.success) {
        // Sort by payment_claimed first, then by creation date
        const sortedBookings = result.data.sort((a, b) => {
          if (a.payment_status === 'payment_claimed' && b.payment_status !== 'payment_claimed') return -1;
          if (b.payment_status === 'payment_claimed' && a.payment_status !== 'payment_claimed') return 1;
          return new Date(b.created_at) - new Date(a.created_at);
        });
        setBookings(sortedBookings);
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (bookingId, newStatus, generateInvoice = false) => {
    try {
      setIsProcessing(true);
      
      const response = await fetch(`http://localhost:5000/api/admin/booking/${bookingId}/payment-status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          paymentStatus: newStatus,
          generateInvoice: generateInvoice,
          verifiedBy: 'admin',
          verifiedAt: new Date().toISOString()
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        // Update local state
        setBookings(prev => prev.map(booking => 
          booking.id === bookingId 
            ? { ...booking, payment_status: newStatus }
            : booking
        ));
        
        if (generateInvoice) {
          alert(`Payment approved and invoice generated for booking ${result.bookingId}`);
        } else {
          alert(`Payment ${newStatus === 'completed' ? 'approved' : 'rejected'} successfully`);
        }
      } else {
        alert('Failed to update payment status: ' + result.message);
      }
    } catch (error) {
      console.error('Error updating payment status:', error);
      alert('Error updating payment status');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkApproval = async () => {
    if (selectedBookings.length === 0) {
      alert('Please select bookings to approve');
      return;
    }

    const confirmed = window.confirm(
      `Approve payment for ${selectedBookings.length} booking(s)? This will generate invoices automatically.`
    );

    if (!confirmed) return;

    try {
      setIsProcessing(true);
      
      const promises = selectedBookings.map(id => 
        handleStatusUpdate(id, 'completed', true)
      );
      
      await Promise.all(promises);
      setSelectedBookings([]);
      
      alert(`${selectedBookings.length} payments approved and invoices generated`);
    } catch (error) {
      alert('Error in bulk approval');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'payment_claimed': { 
        bg: 'bg-yellow-100', 
        text: 'text-yellow-800', 
        icon: Clock, 
        label: 'Verification Required' 
      },
      'completed': { 
        bg: 'bg-green-100', 
        text: 'text-green-800', 
        icon: CheckCircle, 
        label: 'Payment Verified' 
      },
      'pending_payment': { 
        bg: 'bg-red-100', 
        text: 'text-red-800', 
        icon: XCircle, 
        label: 'Payment Required' 
      },
      'pending': { 
        bg: 'bg-gray-100', 
        text: 'text-gray-800', 
        icon: Clock, 
        label: 'Pending' 
      }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon size={12} className="mr-1" />
        {config.label}
      </span>
    );
  };

  const BookingDetailsModal = ({ booking, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <h3 className="text-lg font-bold">Booking Details</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Customer Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-3 flex items-center">
              <User size={16} className="mr-2" />
              Customer Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div><strong>Name:</strong> {booking.name}</div>
              <div><strong>Email:</strong> {booking.email}</div>
              <div><strong>Phone:</strong> {booking.phone}</div>
              <div><strong>Booking ID:</strong> {booking.bookingId || `TMP${booking.id?.toString().padStart(6, '0')}`}</div>
            </div>
          </div>

          {/* Service Information */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-3 flex items-center">
              <Calendar size={16} className="mr-2" />
              Service Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div><strong>Service:</strong> {booking.service}</div>
              <div><strong>Date:</strong> {new Date(booking.booking_date).toLocaleDateString()}</div>
              <div><strong>Time:</strong> {booking.booking_time || 'To be confirmed'}</div>
              <div><strong>Amount:</strong> ₹{booking.payment_amount || 0}</div>
            </div>
            {booking.special_requirements && (
              <div className="mt-3">
                <strong>Special Requirements:</strong>
                <div className="bg-white p-3 rounded mt-1 text-sm">
                  {booking.special_requirements}
                </div>
              </div>
            )}
          </div>

          {/* Payment Information */}
          {booking.payment_amount > 0 && (
            <div className="bg-yellow-50 p-4 rounded-lg">
              <h4 className="font-semibold mb-3 flex items-center">
                <DollarSign size={16} className="mr-2" />
                Payment Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><strong>Amount:</strong> ₹{booking.payment_amount}</div>
                <div><strong>Method:</strong> {booking.payment_method?.toUpperCase()}</div>
                <div><strong>Status:</strong> {getStatusBadge(booking.payment_status)}</div>
                <div><strong>Transaction Ref:</strong> {booking.transaction_ref || 'N/A'}</div>
              </div>
              
              {booking.payment_status === 'payment_claimed' && (
                <div className="mt-4 p-3 bg-yellow-100 border border-yellow-300 rounded">
                  <div className="flex items-center">
                    <AlertTriangle size={16} className="text-yellow-600 mr-2" />
                    <strong className="text-yellow-800">Action Required</strong>
                  </div>
                  <p className="text-yellow-700 text-sm mt-1">
                    Customer claims payment has been made. Please verify with your UPI/bank records.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          {booking.payment_status === 'payment_claimed' && (
            <div className="flex gap-3 pt-4 border-t">
              <button
                onClick={() => {
                  handleStatusUpdate(booking.id, 'completed', true);
                  onClose();
                }}
                disabled={isProcessing}
                className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
              >
                <CheckCircle size={16} className="mr-2" />
                Approve Payment & Generate Invoice
              </button>
              <button
                onClick={() => {
                  handleStatusUpdate(booking.id, 'pending_payment', false);
                  onClose();
                }}
                disabled={isProcessing}
                className="flex-1 bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center"
              >
                <XCircle size={16} className="mr-2" />
                Reject Payment
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin mr-2" size={24} />
        <span>Loading bookings...</span>
      </div>
    );
  }

  const pendingCount = bookings.filter(b => b.payment_status === 'payment_claimed').length;

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Verification Dashboard</h1>
        <p className="text-gray-600">Review and approve payment claims from customers</p>
        
        {pendingCount > 0 && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="text-yellow-600 mr-2" size={20} />
              <span className="text-yellow-800 font-medium">
                {pendingCount} payment{pendingCount > 1 ? 's' : ''} awaiting verification
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search bookings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="payment_claimed">Pending Verification</option>
            <option value="completed">Approved</option>
            <option value="pending_payment">Payment Required</option>
            <option value="pending">Pending</option>
          </select>
        </div>

        <div className="flex gap-2">
          {/* Bulk Actions */}
          {selectedBookings.length > 0 && (
            <button
              onClick={handleBulkApproval}
              disabled={isProcessing}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
            >
              <CheckCircle size={16} className="mr-2" />
              Approve {selectedBookings.length}
            </button>
          )}
          
          <button
            onClick={fetchBookings}
            disabled={loading}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center"
          >
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedBookings(filteredBookings
                          .filter(b => b.payment_status === 'payment_claimed')
                          .map(b => b.id)
                        );
                      } else {
                        setSelectedBookings([]);
                      }
                    }}
                    className="rounded"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredBookings.map((booking) => (
                <tr key={booking.id} className={`hover:bg-gray-50 ${booking.payment_status === 'payment_claimed' ? 'bg-yellow-25' : ''}`}>
                  <td className="px-6 py-4">
                    {booking.payment_status === 'payment_claimed' && (
                      <input
                        type="checkbox"
                        checked={selectedBookings.includes(booking.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedBookings([...selectedBookings, booking.id]);
                          } else {
                            setSelectedBookings(selectedBookings.filter(id => id !== booking.id));
                          }
                        }}
                        className="rounded"
                      />
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{booking.name}</div>
                        <div className="text-sm text-gray-500">{booking.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{booking.service}</div>
                    <div className="text-sm text-gray-500">
                      ID: {booking.bookingId || `TMP${booking.id?.toString().padStart(6, '0')}`}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {new Date(booking.booking_date).toLocaleDateString()}
                    {booking.booking_time && (
                      <div className="text-sm text-gray-500">{booking.booking_time}</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {booking.payment_amount > 0 ? `₹${booking.payment_amount}` : 'Free'}
                    </div>
                    <div className="text-sm text-gray-500">{booking.payment_method || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(booking.payment_status)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowDetailsModal(booking)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      
                      {booking.payment_status === 'payment_claimed' && (
                        <>
                          <button
                            onClick={() => handleStatusUpdate(booking.id, 'completed', true)}
                            disabled={isProcessing}
                            className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                            title="Approve Payment"
                          >
                            <CheckCircle size={16} />
                          </button>
                          <button
                            onClick={() => handleStatusUpdate(booking.id, 'pending_payment', false)}
                            disabled={isProcessing}
                            className="text-red-600 hover:text-red-800 p-1 disabled:opacity-50"
                            title="Reject Payment"
                          >
                            <XCircle size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredBookings.length === 0 && (
            <div className="text-center py-12">
              <FileText className="mx-auto mb-4 text-gray-300" size={48} />
              <p className="text-gray-500">No bookings found</p>
            </div>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && (
        <BookingDetailsModal
          booking={showDetailsModal}
          onClose={() => setShowDetailsModal(null)}
        />
      )}
    </div>
  );
};

export default AdminPaymentApproval;